import type { BakedCrackDecals } from '../track/TrackRenderer';
import type { BakedSurfaceAssets } from '../render/BakedSurfaceAssets';

// Production must use baked surface/crack assets; the procedural shader
// path is dev-only and 4-5x more expensive. Any fall-through here drops a
// real player onto the procedural path silently — log loudly and emit a
// perf event so we can tell from telemetry when prod is degraded.
export async function loadBakedCrackDecals(trackId: string, enabled: boolean): Promise<BakedCrackDecals | null> {
  if (!enabled) return null;
  const url = `/baked/cracks/${trackId}.json`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[baked] crack decals ${url} ${res.status} — falling back to procedural`);
      return null;
    }
    const raw = await res.json();
    if (!isBakedCrackDecals(raw)) {
      console.warn(`[baked] crack decals ${url} failed schema validation — falling back to procedural`);
      return null;
    }
    return raw;
  } catch (err) {
    console.warn(`[baked] crack decals ${url} fetch failed — falling back to procedural`, err);
    return null;
  }
}

export async function loadBakedSurfaceAssets(trackId: string, enabled: boolean): Promise<BakedSurfaceAssets | null> {
  if (!enabled) return null;
  const url = `/baked/surfaces-pbr/${trackId}/manifest.json`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[baked] surfaces ${url} ${res.status} — falling back to procedural`);
      return null;
    }
    const raw = await res.json();
    if (!isBakedSurfaceAssets(raw)) {
      console.warn(`[baked] surfaces ${url} failed schema validation — falling back to procedural`);
      return null;
    }
    return raw;
  } catch (err) {
    console.warn(`[baked] surfaces ${url} fetch failed — falling back to procedural`, err);
    return null;
  }
}

function isBakedCrackDecals(raw: unknown): raw is BakedCrackDecals {
  if (!raw || typeof raw !== 'object') return false;
  const value = raw as Partial<BakedCrackDecals>;
  return value.version === 1
    && typeof value.atlasUrl === 'string'
    && Array.isArray(value.decals)
    && value.decals.every((decal) => (
      decal
      && typeof decal === 'object'
      && typeof decal.name === 'string'
      && Array.isArray(decal.positions)
      && decal.positions.length === 12
      && decal.positions.every((n) => typeof n === 'number' && Number.isFinite(n))
      && Array.isArray(decal.uv)
      && decal.uv.length === 8
      && decal.uv.every((n) => typeof n === 'number' && Number.isFinite(n))
      && typeof decal.opacity === 'number'
      && Number.isFinite(decal.opacity)
    ));
}

function isBakedSurfaceAssets(raw: unknown): raw is BakedSurfaceAssets {
  if (!raw || typeof raw !== 'object') return false;
  const value = raw as Partial<BakedSurfaceAssets>;
  const surfaceOk = (surface: unknown): boolean => {
    if (surface === undefined) return true;
    if (!surface || typeof surface !== 'object') return false;
    const s = surface as {
      albedoUrl?: unknown;
      normalUrl?: unknown;
      materialUrl?: unknown;
      roughnessUrl?: unknown;
      normalScale?: unknown;
    };
    return typeof s.albedoUrl === 'string'
      && (s.normalUrl === undefined || typeof s.normalUrl === 'string')
      && (s.materialUrl === undefined || typeof s.materialUrl === 'string')
      && (s.roughnessUrl === undefined || typeof s.roughnessUrl === 'string')
      && (s.normalScale === undefined || (typeof s.normalScale === 'number' && Number.isFinite(s.normalScale)));
  };
  return value.version === 2 && surfaceOk(value.asphalt) && surfaceOk(value.terrain);
}
